import {AfterViewChecked, Component, ElementRef, inject, OnInit, QueryList, ViewChildren, ChangeDetectionStrategy} from '@angular/core';
import {ChatDialogComponent} from "../../../../shared-module/components/chat-dialog/chat-dialog.component";
import {ButtonComponent} from "../../../../shared-module/components/button/button.component";
import {AsyncPipe} from "@angular/common";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {AuthService} from "../../../../shared-module/services/auth.service";
import {ActivatedRoute, Params} from "@angular/router";
import RelationContact from "../../../../shared-module/model/relation-contact.model";
import {Ticket} from "../../../../shared-module/model/ticket.model";
import {ApplicationConfig, config} from "../../../../../config";
import Relation from "../../../../shared-module/model/relation.model";
import {HeaderService} from "../../../../shared-module/services/header.service";
import {MobileService} from "../../../../shared-module/services/mobile.service";
import {TicketMessage} from "../../../../shared-module/model/ticket-message.model";
import TicketMessageStore from "../../../../shared-module/services/Stores/ticket-message.store";
import TicketStore from "../../../../shared-module/services/Stores/ticket.store";
import {ChatDialogDirective} from "../../../../shared-module/directives/chat-dialog.directive";
import {distinctUntilChanged, of} from "rxjs";

@Component({
    selector: 'app-ticket-detail',
    imports: [
    ChatDialogComponent,
    ButtonComponent,
    FormsModule,
    AsyncPipe,
    ReactiveFormsModule,
    ChatDialogDirective
],
    providers: [
        TicketMessageStore,
    ],
    templateUrl: './ticket-detail.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './ticket-detail.component.scss'
})
export class TicketDetailComponent implements OnInit, AfterViewChecked {
  private authService: AuthService = inject(AuthService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly headerService: HeaderService = inject(HeaderService);
  protected readonly mobileService: MobileService = inject(MobileService);
  private readonly ticketMessageStore: TicketMessageStore = inject(TicketMessageStore);
  private readonly ticketStore: TicketStore = inject(TicketStore);

  public textAreaControl: FormControl = new FormControl();
  public form: FormGroup = new FormGroup({});
  public ticket: Ticket | null = null;
  public currentRelation: Relation | null = null;
  public currentRelationContact: RelationContact | null = null;

  private ticketId: string | null = null;
  private isScrolledFirstTime: boolean = false;
  private isScrolledNewMessage: boolean = false;

  protected readonly config: ApplicationConfig = config;

  @ViewChildren(ChatDialogComponent, {
    read: ElementRef,
    emitDistinctChangesOnly: true
  }) chatDialogs!: QueryList<ElementRef>;

  public ngOnInit(): void {
    this.route.params.subscribe((params: Params) => {
      if (!params['id']) {
        return;
      }

      this.textAreaControl = new FormControl('', [Validators.required, Validators.minLength(1)]);
      this.form = new FormGroup({
        input: this.textAreaControl
      });

      this.ticketId = params['id'];

      this.headerService.set({
        title: this.ticketId ?? '',
        subtitle: 'Bekijk de berichten binnen deze melding',
        showBackButton: true
      });

      this.authService.getActiveRelation().subscribe((relation: Relation | null) => {
        if (!relation) {
          return;
        }

        this.currentRelation = relation;
      });

      this.authService.getUser(['relations.tickets.messages.relation_contact']).subscribe((relationcontact: RelationContact | null) => {
        if (!relationcontact || !this.currentRelation) {
          return;
        }

        this.currentRelationContact = relationcontact;
        const foundTicket: Ticket | null = this.currentRelation.tickets?.find((ticket: Ticket) => ticket.ticket_id === this.ticketId) ?? null;
        if (foundTicket) {
          this.ticket = foundTicket;
          this.ticketMessageStore.reset().forParent("tickets", this.ticket);
          return;
        }

        this.ticketStore.all({}, true).subscribe((tickets: Ticket[]) => {
          this.ticket = tickets.find((ticket: Ticket) => ticket?.ticket_id === this.ticketId) ?? null;
          if (this.ticket) {
            this.ticketMessageStore.reset().forParent("tickets", this.ticket);
          }
        });
      });
    });

    of(this.ticket).pipe(
      distinctUntilChanged(),
    ).subscribe((ticket: Ticket | null) => {
      if (!ticket) {
        return;
      }
      this.ticketStore.markAsRead(ticket);
    })
  }

  public submit(): void {
    if (!this.ticket || !this.ticket?.customer_can_reply || this.form.invalid) {
      return;
    }

    const message: TicketMessage = new TicketMessage();

    message.id = this.ticket?.id ?? 0;

    const regex: RegExp = /(<([^>]+)>)/ig;
    message.description = this.form.controls['input'].value.replace(regex, "");

    this.ticketStore.markAsRead(this.ticket);

    this.ticketMessageStore.create(message, {
      include: "relation_contact"
    }).subscribe((ticketMessage: TicketMessage) => {
      this.ticket?.messages?.push(ticketMessage);
      this.isScrolledNewMessage = true;
    });
    this.form.reset({emitEvent: true});
  }

  /**
   * Scroll to the last message in the chat
   * This method is called after the view is checked
   * and can be used later to scroll to the last message
   * when a new message is added
   */
  public scrollToLastMessage(): boolean {
    if (!this.chatDialogs?.length) {
      return false;
    }
    const lastElement: Element = this.chatDialogs.last.nativeElement;
    lastElement.scrollIntoView({behavior: 'smooth'});
    this.isScrolledFirstTime = true;
    this.isScrolledNewMessage = false;
    return true;
  }

  public ngAfterViewChecked(): void {
    if (!this.isScrolledFirstTime || this.isScrolledNewMessage) {
      this.scrollToLastMessage();
    }
  }
}
